import styled, { css } from "styled-components";
import { fontFamilies } from "components/shared/tokens/typography";

const COLORS = {
  page: "#f5f7fc",
  white: "#ffffff",
  title: "#293142",
  body: "#59647a",
  muted: "#8b94a8",
  lightMuted: "#a7afbf",
  line: "#e1e6ef",
  brand: "#ef4f5f",
  status: "#1479ff",
  statusBackground: "#dce9ff",
};

const MOBILE = "@media (max-width: 480px)";
const PAGE_MAX_WIDTH = "760px";

export const ASNDetailHeader = styled.header`
  display: grid;
  min-height: 104px;
  flex: 0 0 auto;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 18px 28px;
  background: ${COLORS.white};

  ${MOBILE} {
    min-height: 92px;
    gap: 10px;
    padding: 14px 18px;
  }
`;

export const ASNHeaderTitle = styled.h1`
  overflow: hidden;
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 16px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${MOBILE} {
    font-size: 14px;
  }
`;

export const ASNHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  ${MOBILE} {
    gap: 7px;
  }
`;

export const ASNHeaderButton = styled.button`
  display: inline-flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.line};
  border-radius: 50%;
  padding: 0;
  background: ${COLORS.white};
  color: ${COLORS.title};
  cursor: pointer;

  svg {
    width: 23px;
    height: 23px;
    stroke-width: 1.8;
  }

  &:disabled {
    color: ${COLORS.lightMuted};
    cursor: default;
  }

  ${MOBILE} {
    width: 42px;
    height: 42px;

    svg {
      width: 21px;
      height: 21px;
    }
  }
`;

export const ASNDetailStatus = styled.div`
  display: flex;
  min-height: 40px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 0 0 22px 22px;
  background: ${({ $status }) => {
    const status = String($status || "").toUpperCase();
    return status === "FULFILLED" || status === "PARTIALLY_FULFILLED"
      ? "#e4f5f7"
      : status === "CANCELLED" || status === "DELETED" || status === "NO_SHOW"
      ? "#f1f3f7"
      : COLORS.statusBackground;
  }};
  color: ${({ $status }) => {
    const status = String($status || "").toUpperCase();
    return status === "FULFILLED" || status === "PARTIALLY_FULFILLED"
      ? "#087b82"
      : status === "CANCELLED" || status === "DELETED" || status === "NO_SHOW"
      ? COLORS.muted
      : COLORS.status;
  }};
  font-family: ${fontFamilies.semiBold};
  font-size: 14px;

  ${MOBILE} {
    min-height: 36px;
    border-radius: 0 0 18px 18px;
    font-size: 13px;
  }
`;

export const ASNDetailContent = styled.div`
  padding: 26px 28px 42px;

  ${MOBILE} {
    padding: 18px 16px 32px;
  }
`;

export const DeliveryScheduleCard = styled.section`
  display: flex;
  align-items: center;
  gap: 16px;
  border-radius: 22px;
  padding: 18px 20px;
  background: ${COLORS.white};
  box-shadow: 0 3px 12px rgba(41, 49, 66, 0.08);

  ${MOBILE} {
    gap: 12px;
    border-radius: 17px;
    padding: 14px;
  }
`;

export const ScheduleCalendar = styled.span`
  display: inline-flex;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #f1f3f7;
  color: ${COLORS.body};

  svg {
    width: 24px;
    height: 24px;
    stroke-width: 1.8;
  }

  ${MOBILE} {
    width: 44px;
    height: 44px;
    flex-basis: 44px;
  }
`;

export const ScheduleCopy = styled.div`
  min-width: 0;
  flex: 1;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: ${COLORS.title};
    font-family: ${fontFamilies.semiBold};
    font-size: 15px;
  }

  span {
    margin-top: 5px;
    color: ${COLORS.muted};
    font-size: 13px;
  }

  ${MOBILE} {
    strong {
      font-size: 14px;
    }

    span {
      font-size: 12px;
    }
  }
`;

export const LocationButton = styled.a`
  display: inline-flex;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.line};
  border-radius: 50%;
  color: ${COLORS.brand};
  text-decoration: none;

  svg {
    width: 23px;
    height: 23px;
    stroke-width: 1.8;
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      color: ${COLORS.lightMuted};
      pointer-events: none;
    `}

  ${MOBILE} {
    width: 42px;
    height: 42px;
    flex-basis: 42px;
  }
`;

export const ASNSectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 30px 26px 18px;
  color: ${COLORS.lightMuted};
  font-family: ${fontFamilies.bold};
  font-size: 13px;
  letter-spacing: 0.01em;
  line-height: 1.2;
  text-align: center;
  text-transform: uppercase;

  &::before,
  &::after {
    height: 1px;
    flex: 1;
    background: ${COLORS.line};
    content: "";
  }

  span {
    flex: 0 1 auto;
    font-size: inherit;
    font-family: inherit;
  }

  ${MOBILE} {
    gap: 9px;
    margin: 26px 8px 16px;
    font-size: 13px;
  }
`;

export const ShipmentSummaryCard = styled.section`
  overflow: hidden;
  border-radius: 22px;
  background: ${COLORS.white};
  box-shadow: 0 3px 12px rgba(41, 49, 66, 0.08);

  ${MOBILE} {
    border-radius: 17px;
  }
`;

export const ShipmentSummaryRows = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 11px 16px;
  margin: 0;
  padding: 18px 20px;

  dt,
  dd {
    min-width: 0;
    margin: 0;
    line-height: 1.3;
  }

  dt {
    color: ${COLORS.body};
    font-size: 13px;
  }

  dd {
    overflow-wrap: anywhere;
    color: ${COLORS.title};
    font-family: ${fontFamilies.semiBold};
    font-size: 13px;
    text-align: right;
  }

  ${MOBILE} {
    gap: 9px 10px;
    padding: 14px 14px;

    dt,
    dd {
      font-size: 12px;
    }
  }
`;

export const DocumentViewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0 0 0 auto;
  border: 0;
  border-bottom: 1px dotted ${COLORS.brand};
  padding: 0;
  background: transparent;
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: inherit;
  cursor: pointer;

  svg {
    width: 18px;
    height: 18px;
    stroke-width: 1.8;
  }
`;

export const DeliverySummaryFooter = styled.footer`
  display: flex;
  min-height: 78px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-top: 1px solid #f1f3f7;
  padding: 15px 24px;
  background: linear-gradient(90deg, #ffffff 0%, #fafbfe 100%);

  ${MOBILE} {
    min-height: 70px;
    padding: 13px 16px;
  }
`;

export const DeliverySummaryCopy = styled.div`
  min-width: 0;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: ${COLORS.title};
    font-family: ${fontFamilies.semiBold};
    font-size: 14px;
  }

  span {
    margin-top: 4px;
    color: ${COLORS.muted};
    font-size: 12px;
  }

  ${MOBILE} {
    strong {
      font-size: 13px;
    }

    span {
      font-size: 11px;
    }
  }
`;

export const DeliveryCallButton = styled.a`
  display: inline-flex;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  align-items: center;
  justify-content: center;
  border: 1px solid ${COLORS.line};
  border-radius: 50%;
  color: ${COLORS.brand};
  text-decoration: none;

  svg {
    width: 23px;
    height: 23px;
    stroke-width: 1.8;
  }

  ${MOBILE} {
    width: 42px;
    height: 42px;
    flex-basis: 42px;
  }
`;

export const ASNPoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const ASNPoCard = styled.article`
  border-radius: 22px;
  padding: 24px;
  background: ${COLORS.white};
  box-shadow: 0 3px 12px rgba(41, 49, 66, 0.08);

  ${MOBILE} {
    border-radius: 17px;
    padding: 19px 16px;
  }
`;

export const ASNPoTitle = styled.h3`
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 16px;
  line-height: 1.25;
  overflow-wrap: anywhere;

  ${MOBILE} {
    font-size: 15px;
  }
`;

export const ASNPoSubtitle = styled.p`
  margin: 6px 0 0;
  color: ${COLORS.body};
  font-size: 13px;
  line-height: 1.3;

  ${MOBILE} {
    font-size: 12px;
  }
`;

export const InvoiceSectionHeading = styled.h4`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 24px 0 10px;
  color: ${COLORS.lightMuted};
  font-family: ${fontFamilies.semiBold};
  font-size: 15px;
  line-height: 1.2;

  &::after {
    height: 1px;
    flex: 1;
    background: ${COLORS.line};
    content: "";
  }

  ${MOBILE} {
    margin-top: 21px;
    font-size: 12px;
  }
`;

export const ASNInvoiceList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ASNInvoiceRow = styled.div`
  display: grid;
  min-width: 0;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 12px 0;

  ${MOBILE} {
    grid-template-columns: 48px minmax(0, 1fr) auto;
    gap: 10px;
    padding: 10px 0;
  }
`;

export const InvoiceDocumentButton = styled.button`
  display: block;
  width: 58px;
  height: 58px;
  overflow: hidden;
  border: 0;
  border-radius: 9px;
  padding: 0;
  background: #f1f3f7;
  color: ${COLORS.muted};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  /* react-pdf canvas thumbnail */
  .react-pdf__Document,
  .react-pdf__Page,
  .react-pdf__Page__canvas {
    display: block;
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
  }

  .react-pdf__Page__canvas {
    border-radius: 9px;
  }

  ${MOBILE} {
    width: 48px;
    height: 48px;
  }
`;

export const InvoiceDocumentFallback = styled.span`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

export const ASNInvoiceCopy = styled.div`
  min-width: 0;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: ${COLORS.title};
    font-family: ${fontFamilies.bold};
    font-size: 15px;
  }

  span {
    margin-top: 5px;
    color: ${COLORS.body};
    font-size: 13px;
  }

  ${MOBILE} {
    strong {
      font-size: 14px;
    }

    span {
      font-size: 12px;
    }
  }
`;

export const InvoiceAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 4px;
  background: transparent;
  color: ${COLORS.brand};
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    stroke-width: 2;
  }
`;

export const NoGrnBadge = styled.span`
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  border-radius: 6px;
  padding: 0 10px;
  background: #f1f3f7;
  color: ${COLORS.muted};
  font-family: ${fontFamilies.semiBold};
  font-size: 14px;
  white-space: nowrap;

  ${MOBILE} {
    min-height: 30px;
    padding: 0 8px;
    font-size: 12px;
  }
`;

export const ASNBottomActions = styled.footer`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 12px;
  border-top: 1px solid #eef1f6;
  padding: 17px 28px 20px;
  background: ${COLORS.white};
  box-shadow: 0 -7px 20px rgba(41, 49, 66, 0.05);

  ${MOBILE} {
    gap: 10px;
    padding: 13px 16px 16px;
  }
`;

export const CancelShippingNoteButton = styled.button`
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;

  svg {
    width: 17px;
    height: 17px;
  }
`;

export const EditShippingNoteButton = styled.button`
  min-height: 54px;
  border: 0;
  border-radius: 13px;
  background: ${COLORS.brand};
  color: ${COLORS.white};
  font-family: ${fontFamilies.semiBold};
  font-size: 18px;

  ${MOBILE} {
    min-height: 48px;
    font-size: 16px;
  }
`;

export const ASNModalBackdrop = styled.div`
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(20, 24, 32, 0.72);
`;

export const ASNModalCard = styled.section`
  width: 100%;
  max-width: 380px;
  border-radius: 22px;
  background: ${COLORS.white};
  box-shadow: 0 18px 52px rgba(17, 23, 36, 0.32);
`;

export const ASNModalBody = styled.div`
  padding: 34px 26px 30px;
  text-align: center;
`;

export const ASNModalIcon = styled.div`
  display: grid;
  width: 68px;
  height: 68px;
  margin: 0 auto 18px;
  place-content: center;
  border-radius: 50%;
  background: ${({ $tone }) => ($tone === "success" ? "#eaf9ef" : "#fdeee0")};
  color: ${({ $tone }) => ($tone === "success" ? "#149447" : "#c9631f")};

  svg {
    width: 34px;
    height: 34px;
    stroke-width: 2;
  }
`;

export const ASNModalTitle = styled.h2`
  margin: 0;
  color: ${COLORS.title};
  font-family: ${fontFamilies.bold};
  font-size: 19px;
  line-height: 1.35;
`;

export const ASNModalError = styled.p`
  margin: 12px 0 0;
  color: ${COLORS.brand};
  font-size: 13px;
`;

export const ASNModalPrimaryButton = styled.button`
  display: flex;
  width: 100%;
  min-height: 54px;
  margin-top: 26px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 13px;
  background: ${COLORS.brand};
  color: ${COLORS.white};
  font-family: ${fontFamilies.semiBold};
  font-size: 17px;
  cursor: pointer;

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;

export const ASNModalTextButton = styled.button`
  display: block;
  width: 100%;
  min-height: 46px;
  margin-top: 6px;
  border: 0;
  background: transparent;
  color: ${COLORS.brand};
  font-family: ${fontFamilies.semiBold};
  font-size: 16px;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

export const ASNModalDivider = styled.div`
  border-top: 1px solid ${COLORS.line};
  margin-top: 26px;
`;
